<template>
  <div class="kerr-effect-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$Δn = K · λ · E²$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <linearGradient id="beamGrad" x1="0" y1="0.5" x2="1" y2="0.5">
                <stop offset="0%" stop-color="rgba(255,255,255,0.05)" />
                <stop offset="40%" stop-color="rgba(255,255,255,0.15)" />
                <stop offset="100%" stop-color="rgba(255,255,255,0.05)" />
             </linearGradient>
             <filter id="optics-glow"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- Optical Rail -->
          <line x1="20" y1="120" x2="380" y2="120" stroke="url(#beamGrad)" stroke-width="20" stroke-linecap="round" />

          <!-- Component 1: Polarizer -->
          <g transform="translate(60, 120)">
             <circle r="35" fill="#1e293b" stroke="#64748b" stroke-width="2" />
             <line x1="0" y1="-25" x2="0" y2="25" stroke="#f8fafc" stroke-width="3" />
             <text y="48" fill="#94a3b8" font-size="10" text-anchor="middle">起偏器 P (0°)</text>
          </g>

          <!-- Component 2: Kerr Cell -->
          <g transform="translate(150, 80)">
             <rect width="100" height="80" fill="rgba(56, 189, 248, 0.05)" stroke="#38bdf8" stroke-width="1" rx="4" />
             <!-- Electrodes -->
             <rect x="5" y="0" width="90" height="6" :fill="voltage > 0 ? '#fbbf24' : '#64748b'" />
             <rect x="5" y="74" width="90" height="6" :fill="voltage > 0 ? '#fbbf24' : '#64748b'" />
             
             <!-- Induced Birefringence Visualization (Waves) -->
             <path v-for="i in 3" :key="'w'+i" :d="generateBiref(i)" 
                   fill="none" :stroke="voltage > 0 ? '#38bdf8' : 'none'" 
                   :stroke-width="1" :opacity="0.3 + i*0.2" />

             <text x="50" y="45" fill="#38bdf8" font-size="10" font-weight="bold" text-anchor="middle">克尔介质 (各向同性→双折射)</text>
             <text x="50" y="-10" :fill="voltage > 0 ? '#fbbf24' : '#64748b'" font-size="10" font-weight="900" text-anchor="middle">
                外加电场 E
             </text>
          </g>

          <!-- Component 3: Analyzer (90° crossed) -->
          <g transform="translate(320, 120)">
             <circle r="35" fill="#1e293b" stroke="#64748b" stroke-width="2" />
             <line x1="-25" y1="0" x2="25" y2="0" stroke="#f8fafc" stroke-width="3" />
             <!-- Passing intensity glow -->
             <circle r="15" :fill="intensityColor" filter="url(#optics-glow)" />
             <text y="48" fill="#94a3b8" font-size="10" text-anchor="middle">检偏器 A (90°)</text>
          </g>

          <!-- Progress Arrows -->
          <path d="M 100,120 L 140,120" stroke="#64748b" stroke-width="1.5" marker-end="url(#kh-arr-rail)" />
          <path d="M 260,120 L 300,120" stroke="#64748b" stroke-width="1.5" marker-end="url(#kh-arr-rail)" />

          <defs>
              <marker id="kh-arr-rail" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                 <path d="M0,0 L6,2 L0,4 Z" fill="#64748b" />
              </marker>
          </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>相位延迟 Δφ</label>
              <span class="val">{{ phaseShift.toFixed(2) }} <small>rad</small></span>
           </div>
           <div class="hud-item orange">
              <label>输出光强 I</label>
              <span class="val">{{ (intensity * 100).toFixed(1) }} %</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item" style="grid-column: span 2;">
                 <label>调节电压 V (产生外加静电场 E)</label>
                 <el-slider v-model="voltage" :min="0" :max="120" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理详解：</strong>克尔效应揭示了**电学对光学**的精准操控。当给克尔盒（液体或晶体）施加电场时，介质原子会发生极化重排，使得它在不同方向上的折射率发生微小偏差。光线穿过时，其相互垂直的偏振分量会产生相位差。在正交偏振片组中，你会看到原本被“锁死”的光线随电压平方级迅速漏出。它是超高速光学开关（响应快至皮秒）的基础，广泛应用于激光调Q和高速摄影。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KerrEffectViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      voltage: 0,
      time: 0,
      timer: null
    }
  },
  computed: {
    phaseShift() {
       // Delta_phi is proportional to L * E^2. k = constant
       return Math.pow(this.voltage / 80, 2) * Math.PI;
    },
    intensity() {
       // Malus law variation for crossed polarizers with retardation
       // I = I0 * sin^2(Delta_phi / 2)
       return Math.pow(Math.sin(this.phaseShift / 2), 2);
    },
    intensityColor() {
       return `rgba(56, 189, 248, ${this.intensity})`;
    }
  },
  methods: {
    generateBiref(idx) {
       let d = "M 10,40 ";
       for(let x=10; x<90; x+=5) {
          let y = 40 + Math.sin(x * 0.2 + this.time * 0.1 + idx) * (this.voltage/10);
          d += `L ${x} ${y} `;
       }
       return d;
    },
    animate() {
       this.time += 1;
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.kerr-effect-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fdf2f8; color: #db2777; border: 1px solid #fbcfe8; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.discovery-card-compact { background: #fdf2f8; padding: 10px 14px; border-radius: 10px; border: 1px solid #fbcfe8; display: flex; align-items: flex-start; gap: 10px; i { color: #db2777; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #9d174d; line-height: 1.5; strong { color: #831843; } } }
</style>
